import * as Haptic from "expo-haptics";

import {
  AHIcon,
  AHText,
  AHTextFonts,
  Box,
  TouchableBox,
  useScaleFactor,
  useTheme,
} from "arkh";
import { Platform, StyleSheet, TouchableOpacity, View } from "react-native";
import React, { useCallback, useMemo } from "react";

import { ResolvedCircle } from "./ResolvedCircle";
import { graphql, useFragment, useMutation } from "react-relay";
import { WidgetIssueOverviewIssueItem_issue$key } from "./__generated__/WidgetIssueOverviewIssueItem_issue.graphql";
import { WidgetIssueOverviewIssueItemAssignees_issue$key } from "./__generated__/WidgetIssueOverviewIssueItemAssignees_issue.graphql";
import { WidgetIssueOverviewIssueItemTitles_issue$key } from "./__generated__/WidgetIssueOverviewIssueItemTitles_issue.graphql";
import { WidgetIssueOverviewIssueItemSectionTitle_issue$key } from "./__generated__/WidgetIssueOverviewIssueItemSectionTitle_issue.graphql";
import { useEvent } from "../useEvent";
import { WidgetIssueOverviewIssueItemToggleResolvedMutation } from "./__generated__/WidgetIssueOverviewIssueItemToggleResolvedMutation.graphql";
import { WidgetIssueOverviewIssueItemContent_issue$key } from "./__generated__/WidgetIssueOverviewIssueItemContent_issue.graphql";
import { FormattedMessage } from "react-intl";

export function useTaskExecutionIssueHeight() {
  const sf = useScaleFactor();
  return sf * 40;
}

function WidgetIssueOverviewIssueItemComponent(props: {
  issue: WidgetIssueOverviewIssueItem_issue$key;
  isLast: boolean;
  isFocused?: boolean;
  mode?: "FOCUS" | "IDLE";
  onPress?: (id: string) => void;
}) {
  const issue = useFragment(
    graphql`
      fragment WidgetIssueOverviewIssueItem_issue on Issue {
        id
        resolved
        ...WidgetIssueOverviewIssueItemContent_issue
      }
    `,
    props.issue
  );
  const sf = useScaleFactor();
  const height = useTaskExecutionIssueHeight();
  const borderLeftWidth = Math.floor(sf * 4);

  const theme = useTheme();

  const onPress = useCallback(() => {
    props.onPress?.(issue.id);
  }, [issue.id, props]);

  return (
    <TouchableBox
      color={props.isFocused ? "background" : "surface"}
      onPress={onPress}
    >
      <View
        style={{
          opacity: issue.resolved ? 0.5 : 1,
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: props.isLast ? "transparent" : theme.surfaceBorder,
          borderLeftWidth,
          borderLeftColor:
            props.isFocused && props.mode === "FOCUS"
              ? theme.primary
              : "transparent",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          position: "relative",
          height,
        }}
      >
        <WidgetIssueOverviewIssueItemContent
          issue={issue}
          paddingLeft={-borderLeftWidth}
        />
      </View>
    </TouchableBox>
  );
}

function WidgetIssueOverviewIssueItemContent(props: {
  issue: WidgetIssueOverviewIssueItemContent_issue$key;
  paddingLeft: number;
}) {
  const issue = useFragment(
    graphql`
      fragment WidgetIssueOverviewIssueItemContent_issue on Issue {
        id
        resolved
        ...WidgetIssueOverviewIssueItemImageThumbanil_issue
        ...WidgetIssueOverviewIssueItemTitles_issue
        ...WidgetIssueOverviewIssueItemAssignees_issue
        ...WidgetIssueOverviewIssueItemSectionTitle_issue
      }
    `,
    props.issue
  );

  const [_toggleResolve] =
    useMutation<WidgetIssueOverviewIssueItemToggleResolvedMutation>(
      graphql`
        mutation WidgetIssueOverviewIssueItemToggleResolvedMutation(
          $issueId: ID!
          $isResolved: Boolean!
        ) @raw_response_type {
          issue(id: $issueId) {
            setResolved(isResolved: $isResolved) {
              issue {
                id
                resolved
              }
            }
          }
        }
      `
    );

  const toggleResolve = useEvent(() => {
    const nextResolved = !issue.resolved;
    _toggleResolve({
      variables: {
        issueId: issue.id,
        isResolved: nextResolved,
      },
      optimisticResponse: {
        issue: {
          setResolved: {
            issue: {
              id: issue.id,
              resolved: nextResolved,
            },
          },
        },
      },
    });
  });

  const sf = useScaleFactor();
  return (
    <>
      <Box
        spaceLeft="medium"
        style={{
          flexDirection: "row",
          alignItems: "center",
          flex: 1,
          marginLeft: props.paddingLeft,
        }}
      >
        <TouchableOpacity
          hitSlop={{ top: 10 * sf, left: 40, bottom: 10 * sf, right: 30 * sf }}
          onPressIn={() => {
            if (Platform.OS === "ios") {
              Haptic.selectionAsync();
            }
          }}
          onPress={toggleResolve}
        >
          <ResolvedCircle isResolved={issue.resolved} />
        </TouchableOpacity>
        <Box
          spaceLeft="small"
          pointerEvents="none"
          spaceRight="xsmall"
          style={{
            flex: 1,
            justifyContent: "center",
          }}
        >
          <Titles issue={issue} />
          <Assignees issue={issue} />
        </Box>
      </Box>
      <ImageThumbnail issue={issue} />
      <SectionTitle issue={issue} />
    </>
  );
}

function ImageThumbnail(props: { issue: any }) {
  const issue = useFragment(
    graphql`
      fragment WidgetIssueOverviewIssueItemImageThumbanil_issue on Issue {
        images {
          id
        }
      }
    `,
    props.issue
  );
  return issue.images.length > 0 ? (
    <AHIcon type="image" size="body" spaceRight="small" />
  ) : null;
}

function SectionTitle(props: {
  issue: WidgetIssueOverviewIssueItemSectionTitle_issue$key;
}) {
  const issue = useFragment(
    graphql`
      fragment WidgetIssueOverviewIssueItemSectionTitle_issue on Issue {
        taskExecution {
          sectionVersion {
            title
          }
        }
      }
    `,
    props.issue
  );

  return (
    <Box pointerEvents="none" spaceRight="small">
      <AHText font={AHTextFonts.BOLD} type="detail">
        {(issue.taskExecution.sectionVersion.title ?? "").length > 0
          ? `${issue.taskExecution.sectionVersion.title}`
          : null}
      </AHText>
    </Box>
  );
}

function Titles(props: {
  issue: WidgetIssueOverviewIssueItemTitles_issue$key;
}) {
  const issue = useFragment(
    graphql`
      fragment WidgetIssueOverviewIssueItemTitles_issue on Issue {
        task {
          title
        }
        title
      }
    `,
    props.issue
  );

  return (
    <AHText
      style={{ overflow: "hidden" }}
      numberOfLines={2}
      spaceRight="xsmall"
      type="subtitle"
    >
      {issue.task.title}
      <AHText type="body">
        {" "}
        {issue.title ? (
          issue.title
        ) : (
          <FormattedMessage
            description="WidgetIssueOverview Empty issue label"
            defaultMessage="Issue"
          />
        )}
      </AHText>
    </AHText>
  );
}
function Assignees(props: {
  issue: WidgetIssueOverviewIssueItemAssignees_issue$key;
}) {
  const issue = useFragment(
    graphql`
      fragment WidgetIssueOverviewIssueItemAssignees_issue on Issue {
        assignedUsers {
          user {
            name
          }
        }
      }
    `,
    props.issue
  );

  const assigneeText = useMemo(
    () =>
      issue.assignedUsers
        .filter((user) => user)
        .map(({ user }) => {
          const names = user?.name.split(" ") ?? [""];
          return `${names[0]}${
            names.length > 1 ? " " + names[1].charAt(0) : ""
          }`;
        })
        .join(" · "),
    [issue.assignedUsers]
  );

  if (!assigneeText) {
    return null;
  }

  return (
    <AHText
      numberOfLines={1}
      type="detail"
      style={{
        alignItems: "center",
      }}
      spaceRight="small"
    >
      <AHIcon color="darkText" type="users" size="detail" /> {assigneeText}
    </AHText>
  );
}

export const TaskExecutionIssue = React.memo(
  WidgetIssueOverviewIssueItemComponent
);
